import { useAuth } from '../contexts/AuthContext';
import { useCallback } from 'react';

/**
 * Custom hook for handling current user data consistently across the application
 * Handles edge cases like user.id = 0 (Super Administrator) and provides standardized methods
 */
export const useCurrentUser = () => {
  const { user } = useAuth();
  
  // Check if user has a valid ID (including 0 for Super Administrator)
  const hasValidUser = user && typeof user.id === 'number' && user.id >= 0;
  
  /**
   * Get user ID safely, handling the case where user.id = 0
   * Special handling: If user.id = 0 (superadmin from auth), map to actual database user ID
   * @returns number | null
   */
  const getUserId = useCallback((): number | null => {
    if (!hasValidUser) return null;
    
    console.log('🔍 getUserId debug:', { 
      userId: user.id, 
      username: user.username, 
      is_superadmin: user.is_superadmin,
      userObject: user 
    });
    
    // Handle superadmin ID mapping for APIs that need database user ID
    // Auth system uses ID 0 for superadmin, but some APIs may need ID 47
    if ((user.id === 0 || user.id === null) && 
        (user.username === 'superadmin' || user.is_superadmin === true)) {
      console.log('🔄 UserID mapping: superadmin (0) -> Will be handled by backend');
      return 0; // Return original ID, let backend handle mapping if needed
    }
    
    console.log('🔄 UserID mapping: using original ID', user.id);
    return user.id;
  }, [hasValidUser, user]);
  
  /**
   * Get user display name with fallback priority
   * Priority: full_name -> name -> username -> email
   * @returns string
   */
  const getUserName = useCallback((): string => {
    if (!user) return '';
    return user.full_name || user.name || user.username || user.email || '';
  }, [user]);
  
  /**
   * Get user email
   * @returns string
   */
  const getUserEmail = useCallback((): string => {
    return user?.email || '';
  }, [user]);
  
  /**
   * Check if user is authenticated and has valid data
   * @returns boolean
   */
  const isAuthenticated = useCallback((): boolean => {
    return !!hasValidUser;
  }, [hasValidUser]);
  
  /**
   * Get user role safely
   * @returns string
   */
  const getUserRole = useCallback((): string => {
    return user?.role || '';
  }, [user]);
  
  /**
   * Check if user is super administrator
   * @returns boolean
   */
  const isSuperAdmin = useCallback((): boolean => {
    return user?.is_superadmin === true || user?.role === 'superadmin';
  }, [user]);
  
  /**
   * Get complete user info for forms
   * @returns object with commonly used user data
   */
  const getUserFormData = useCallback(() => {
    return {
      userId: getUserId(),
      userName: getUserName(),
      userEmail: getUserEmail(),
      userRole: getUserRole()
    };
  }, [getUserId, getUserName, getUserEmail, getUserRole]);
  
  return {
    user,
    hasValidUser,
    getUserId,
    getUserName,
    getUserEmail,
    getUserRole,
    isAuthenticated,
    isSuperAdmin,
    getUserFormData
  };
};

export default useCurrentUser;